import type { ReactNode } from "react";
import { cn } from "@marketing-sites/utils/cn";
import { NavigationMenu } from "@base-ui/react/navigation-menu";
import { Menu } from "@base-ui/react/menu";
import type {
  NavItem,
  NavLink,
  NavLinkFullWidthRow,
  NavLinkGrid,
  NavLinkProse,
  NavLinkSection,
} from "../../types";
import { useHeaderContext } from "../../context";
import { HeaderLink } from "../header-link";
import { Text } from "../text";
import { Button } from "../button";
import { Tooltip } from "../tooltip";
import { ChevronDownIcon } from "../../icons/chevron-down-icon";
import { ChevronDownOutlineIcon } from "../../icons/chevron-down-outline-icon";
import { RightArrowIcon } from "../../icons/right-arrow-icon";
import {
  navFg,
  navFgMuted,
  navSubheadFg,
  navPanelBg,
  navPanelBorder,
  navHoverBg,
  floatingUnderlineBase,
  floatingUnderlineGroupHover,
  floatingUnderlineSelfHover,
} from "../floating-nav-classes";
import { isOffDomain } from "../is-off-domain";

// Prototype nav labels are a flat 14px; `btnLg` scales to 16px at desktop.
const floatingNavLabelClass = "text-[14px] leading-[1.1]";

type NavLinkSectionMember = NavLinkSection["links"][number];

const isNavLinkGrid = (link: NavLinkSectionMember): link is NavLinkGrid =>
  "columns" in link;
const isNavLinkProse = (link: NavLinkSectionMember): link is NavLinkProse =>
  "prose" in link;
const isNavLinkFullWidthRow = (
  link: NavLinkSectionMember,
): link is NavLinkFullWidthRow => "fullWidthRow" in link;
const isNavLink = (link: NavLinkSectionMember): link is NavLink =>
  !isNavLinkGrid(link) && !isNavLinkProse(link) && !isNavLinkFullWidthRow(link);

// A single dropdown link without a description (also used inside grids)
const IconOnlyLink = ({ link }: { link: NavLink }) => (
  <HeaderLink
    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
    analyticsId={`${link.analyticsId}_desktop`}
    href={link.href}
    text={link.title}
    analyticsText={link.analyticsText}
    external={link.external}
    className="z-1 group relative flex w-full items-center gap-2"
  >
    {link.icon && (
      <div className="duration-400 ease-out-quart transition-opacity group-hover:opacity-60">
        {link.icon}
      </div>
    )}
    <Text
      variant="btnLg"
      anchor
      animateIn
      className="text-nav-content-primary mt-1.5"
    >
      {link.title}
    </Text>
  </HeaderLink>
);

// A grid of links laid out across N columns
const NavLinkGridItem = ({ grid }: { grid: NavLinkGrid }) => (
  <li
    className="grid gap-4"
    style={{ gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))` }}
  >
    {grid.links.map((link, index) => (
      <IconOnlyLink key={index} link={link} />
    ))}
  </li>
);

// A standard dropdown link (with or without description)
const NavLinkItem = ({ link }: { link: NavLink }) => {
  if (!link.description) {
    return (
      <li>
        <IconOnlyLink link={link} />
      </li>
    );
  }
  return (
    <li className="mb-3.5 last-of-type:mb-0">
      <HeaderLink
        // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
        analyticsId={`${link.analyticsId}_desktop`}
        href={link.href}
        text={link.title}
        analyticsText={link.analyticsText}
        external={link.external}
        className="z-1 after:duration-400 after:ease-out-quart hover:after:bg-nav-hover after:-z-1 group relative flex w-full items-start gap-2.5 after:absolute after:-inset-2 after:rounded-md after:transition-colors"
      >
        {link.icon && (
          <div className="bg-nav-content-primary/5 duration-400 ease-out-quart group-hover:bg-nav-content-primary group-hover:text-nav-control aspect-square w-10 shrink-0 rounded-sm transition-colors">
            {link.icon}
          </div>
        )}
        {link.image && (
          <div className="aspect-square w-10 shrink-0 overflow-hidden rounded-sm">
            {link.image}
          </div>
        )}
        <span className="mr-auto flex flex-col gap-1">
          <Text
            variant="btnLg"
            className="text-nav-content-primary inline-flex items-center gap-2"
          >
            {link.title}
          </Text>
          <Text variant="bodyMd" className="text-nav-content-tertiary block">
            {link.description}
          </Text>
        </span>
      </HeaderLink>
    </li>
  );
};

type DesktopNavItemProps = {
  item: NavItem;
  dataTestId?: string;
};

// Helper: Renders the dropdown columns
const DropdownLinks = ({ links }: { links: NavLinkSection[][] }) => (
  <div
    className="grid w-full"
    style={{
      gridTemplateColumns: `repeat(${Math.min(3, links.length)}, minmax(0, 1fr))`,
    }}
  >
    {links.map((section, index) => (
      <div
        key={index}
        className="nth-of-type-4:border-l-0 nth-of-type-4:col-span-full nth-of-type-4:border-t border-nav-border/30 flex shrink-0 flex-col gap-6 border-l p-8 first:border-l-0"
      >
        {section.map((section2) => (
          <div key={`${section2.title}-${index}`}>
            {section2.title && (
              <Text
                variant="labelSm"
                className="text-nav-content-tertiary mb-4 block font-normal"
              >
                {section2.title}
              </Text>
            )}
            <ul className="flex flex-col gap-4">
              {section2.links.map((link, linkIndex) => {
                if (isNavLinkGrid(link))
                  return <NavLinkGridItem key={linkIndex} grid={link} />;
                if (isNavLink(link))
                  return <NavLinkItem key={linkIndex} link={link} />;
                // prose / fullWidthRow members are floating-variant only and
                // never appear in default fixtures.
                return null;
              })}
            </ul>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const SmallDropdownLinks = ({ links }: { links: NavLinkSection[][] }) => (
  <div>
    {links.map((sections) =>
      sections.map((section) =>
        section.links.map((link, linkIndex) => {
          if (!isNavLink(link)) return null;
          return (
            <HeaderLink
              // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
              analyticsId={`${link.analyticsId}_desktop`}
              key={linkIndex}
              href={link.href}
              text={link.title}
              analyticsText={link.analyticsText}
              external={link.external}
              className="block p-4"
            >
              <Text variant="btnLg" className="text-nav-content-primary block">
                {link.title}
              </Text>
            </HeaderLink>
          );
        }),
      ),
    )}
  </div>
);

// --- Floating variant (redesigned Fin nav) -------------------------------
// Compact per-item dropdown panels. Rendered only when the header `variant`
// is "floating"; the default variant is untouched.

const floatingAccentBg: Record<
  NonNullable<NavLinkFullWidthRow["accent"]>,
  string
> = {
  chalk:
    "from-[var(--nav-hover,var(--color-chalk))] to-[var(--nav-hover,var(--color-chalk))]",
  fin: "from-fin to-fin",
  helpdesk: "from-helpdesk to-helpdesk",
  // Filled by the artwork overlay in FloatingFullWidthRow instead.
  pioneer: "",
};

const FloatingPrimaryLink = ({ link }: { link: NavLink }) => (
  <HeaderLink
    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
    analyticsId={`${link.analyticsId}_desktop`}
    href={link.href}
    text={link.title}
    analyticsText={link.analyticsText}
    external={link.external}
    suppressExternalIcon
    className={cn(
      navHoverBg,
      "group -mx-3 -my-2 flex w-[calc(100%+1.5rem)] flex-col rounded-md px-3 py-2 transition-colors duration-150",
    )}
  >
    <span
      className={cn(
        navFg,
        "flex w-full items-start justify-between text-[16px] font-medium leading-[1.1] tracking-[-0.16px]",
      )}
    >
      {link.title}
      <span
        aria-hidden
        className={cn(
          "transition-transform duration-150 ease-out",
          link.external
            ? "group-hover:-translate-y-[3px] group-hover:translate-x-[3px]"
            : "group-hover:translate-x-[3px]",
        )}
      >
        {link.external ? "↗" : "→"}
      </span>
    </span>
    {link.description && !link.descriptionMobileOnly && (
      <span
        className={cn(
          navSubheadFg,
          "text-[15px] leading-[1.4] tracking-[-0.15px]",
        )}
      >
        {link.description}
      </span>
    )}
  </HeaderLink>
);

const FloatingSecondaryLink = ({ link }: { link: NavLink }) => {
  const { homeDomain } = useHeaderContext();
  const showArrow = link.external || isOffDomain(link.href, homeDomain);
  return (
    <HeaderLink
      // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
      analyticsId={`${link.analyticsId}_desktop`}
      href={link.href}
      text={link.title}
      analyticsText={link.analyticsText}
      external={link.external}
      suppressExternalIcon
      className="group/link -mx-2 -my-1.5 flex w-[calc(100%+1rem)] flex-col px-2 py-1.5"
    >
      <span
        className={cn(
          navFg,
          "inline-flex w-fit items-center gap-1 text-[14px] leading-[1.1]",
        )}
      >
        <span
          className={cn(floatingUnderlineBase, floatingUnderlineGroupHover)}
        >
          {link.title}
        </span>
        {showArrow && (
          <span
            aria-hidden
            className="translate-y-[1px] opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover/link:-translate-y-[1px] group-hover/link:opacity-100"
          >
            ↗
          </span>
        )}
      </span>
      {link.description && !link.descriptionMobileOnly && (
        <span className={cn(navFgMuted, "text-[14px] leading-[1.4]")}>
          {link.description}
        </span>
      )}
    </HeaderLink>
  );
};

// Wraps a keyline-group row so hovering anywhere in it tints that row's
// segment of the shared left keyline orange. `insetY` trims the tint to the
// text height on rows whose link has vertical padding (py-1.5).
const KeylineRow = ({
  children,
  insetY,
}: {
  children: ReactNode;
  insetY?: boolean;
}) => (
  <div className="group/secondary relative">
    <span
      aria-hidden
      className={cn(
        "absolute -left-[9px] w-[0.5px] transition-colors duration-150 group-hover/secondary:bg-[var(--nav-keyline-accent,var(--color-fin))]",
        insetY ? "bottom-1.5 top-1.5" : "inset-y-0",
      )}
    />
    {children}
  </div>
);

const FloatingProse = ({ prose }: { prose: NavLinkProse["prose"] }) => (
  <p className={cn(navFgMuted, "w-full text-[14px] leading-[1.4]")}>
    {prose.map((segment, index) =>
      typeof segment === "string" ? (
        <span key={index}>{segment}</span>
      ) : (
        <HeaderLink
          // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
          analyticsId={`${segment.analyticsId}_desktop`}
          key={index}
          href={segment.href}
          text={segment.label}
          analyticsText={segment.analyticsText}
          external={segment.external}
          className={cn(
            "inline",
            floatingUnderlineBase,
            floatingUnderlineSelfHover,
            navFg,
          )}
        >
          {segment.label}
        </HeaderLink>
      ),
    )}
  </p>
);

const FloatingFullWidthRow = ({ row }: { row: NavLinkFullWidthRow }) => {
  const isLarge = row.size === "large";
  const hasArtwork = row.accent === "pioneer";
  const labelSpan = (
    <span
      className={cn(
        navFg,
        "text-[16px] font-medium leading-[1.1] tracking-[-0.16px]",
      )}
    >
      {row.label}
    </span>
  );
  return (
    <HeaderLink
      // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the row's authored id; every nav row names itself with a literal, never copy or a translated string
      analyticsId={`${row.analyticsId}_desktop`}
      href={row.href}
      text={row.label}
      analyticsText={row.analyticsText}
      external={row.external}
      className={cn(
        navPanelBorder,
        navPanelBg,
        "group flex w-full items-center justify-between gap-6 border-t-[0.5px] bg-gradient-to-t bg-[length:100%_0%] bg-[position:0%_100%] bg-no-repeat px-9 transition-[background-size] duration-200 ease-out hover:bg-[length:100%_100%]",
        isLarge ? "py-5" : "py-[18px]",
        hasArtwork && "relative isolate overflow-hidden",
        floatingAccentBg[row.accent ?? "chalk"],
      )}
    >
      {hasArtwork && (
        // Clip-path wipe, not the background-size one, so the artwork isn't stretched.
        <span
          aria-hidden
          className="absolute inset-0 -z-10 bg-[image:var(--nav-pioneer-artwork)] bg-cover bg-center transition-[clip-path] duration-200 ease-out [clip-path:inset(100%_0_0_0)] group-hover:[clip-path:inset(0)]"
        />
      )}
      <span className={cn("flex items-center gap-4", isLarge && "gap-5")}>
        {row.icon}
        {row.description ? (
          <span className="flex flex-col">
            {labelSpan}
            <span
              className={cn(
                navSubheadFg,
                isLarge
                  ? "text-[15px] leading-[1.4] tracking-[-0.15px]"
                  : "text-[14px] leading-[1.4]",
              )}
            >
              {row.description}
            </span>
          </span>
        ) : (
          labelSpan
        )}
      </span>
      <span
        aria-hidden
        className={cn(
          navFg,
          isLarge && "text-[18px]",
          "transition-transform duration-150 ease-out group-hover:translate-x-[3px]",
        )}
      >
        →
      </span>
    </HeaderLink>
  );
};

const FloatingSection = ({ section }: { section: NavLinkSection }) => {
  const secondaryLinks = (
    <ul
      className={cn("flex flex-col", section.spacious ? "gap-9" : "gap-[18px]")}
    >
      {section.links.map((link, index) => {
        if (isNavLinkFullWidthRow(link)) return null;
        if (isNavLinkGrid(link))
          return <NavLinkGridItem key={index} grid={link} />;
        const isProse = isNavLinkProse(link);
        const inner = isProse ? (
          <FloatingProse prose={link.prose} />
        ) : link.tier === "primary" ? (
          <FloatingPrimaryLink link={link} />
        ) : (
          <FloatingSecondaryLink link={link} />
        );
        return (
          <li key={index}>
            {section.keyline ? (
              <KeylineRow insetY={!isProse}>{inner}</KeylineRow>
            ) : (
              inner
            )}
          </li>
        );
      })}
    </ul>
  );
  return (
    <div className="flex flex-col gap-[18px]">
      {section.title && (
        <p
          className={cn(
            navFg,
            "w-full text-[16px] font-medium leading-[1.1] tracking-[-0.16px]",
          )}
        >
          {section.title}
        </p>
      )}
      {section.keyline ? (
        <div
          className={cn(navPanelBorder, "flex flex-col border-l-[0.5px] pl-2")}
        >
          {secondaryLinks}
        </div>
      ) : (
        secondaryLinks
      )}
    </div>
  );
};

const isRowOnlyColumn = (column: NavLinkSection[]) =>
  column.some((section) => section.links.some(isNavLinkFullWidthRow)) &&
  column.every((section) => section.links.every(isNavLinkFullWidthRow));

const columnRows = (column: NavLinkSection[]) =>
  column.flatMap((section) => section.links.filter(isNavLinkFullWidthRow));

const FloatingColumn = ({ column }: { column: NavLinkSection[] }) => (
  <div className={cn(navPanelBg, "flex w-[320px] flex-col p-9")}>
    {column.map((section, index) => (
      <div
        key={index}
        className={cn(
          index > 0 &&
            (section.keyline && !section.title ? "mt-[18px]" : "mt-9"),
        )}
      >
        <FloatingSection section={section} />
      </div>
    ))}
  </div>
);

const FloatingDropdownLinks = ({ links }: { links: NavLinkSection[][] }) => {
  const columns = links.filter((column) => !isRowOnlyColumn(column));
  const rows = links.filter(isRowOnlyColumn).flatMap(columnRows);
  return (
    <div className={cn(navPanelBg, "flex flex-col")}>
      {columns.length > 0 && (
        <div className="flex divide-x-[0.5px] divide-[var(--nav-panel-border,var(--color-clay))]">
          {columns.map((column, index) => (
            <FloatingColumn key={index} column={column} />
          ))}
        </div>
      )}
      {rows.map((row, index) => (
        <FloatingFullWidthRow key={index} row={row} />
      ))}
    </div>
  );
};

// Custom item with dropdown
const CustomItemWithDropdown = ({
  item,
  dataTestId,
}: {
  item: Extract<NavItem, { type: "customWithDropdown" }>;
  dataTestId?: string;
}) => (
  <NavigationMenu.Item data-testid={dataTestId}>
    <Menu.Root>
      <Menu.Trigger
        openOnHover
        delay={0}
        className={cn(
          "flex h-10 items-center whitespace-nowrap rounded-md",
          "hover:bg-nav-hover data-popup-open:bg-nav-hover",
        )}
      >
        {item.customItem}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={12}>
          <Menu.Popup className="border-nav-border/30 bg-nav-background rounded-md border">
            <SmallDropdownLinks links={item.links} />
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  </NavigationMenu.Item>
);

// Button item
const ButtonItem = ({
  item,
  dataTestId,
}: {
  item: Extract<NavItem, { type: "button" }>;
  dataTestId?: string;
}) => {
  const { variant: headerVariant } = useHeaderContext();
  const floating = headerVariant === "floating";
  return (
    <NavigationMenu.Item className="ml-2 first:ml-0" data-testid={dataTestId}>
      {(() => {
        const tooltipId =
          item.tooltip && !floating
            ? `tooltip-${item.label.toLowerCase().replace(/\s+/g, "-")}`
            : undefined;
        const button = (
          <Button
            variant={item.variant ?? "primary"}
            size={item.size ?? "base"}
            isExternal={floating ? false : item.isExternal}
            className={cn(
              floating &&
                "h-10 gap-1 rounded-[4px] px-3 py-0 text-[14px] leading-[1.1] min-[1140px]:px-3 xl:px-3 xl:py-0 xl:text-[14px]",
              item.className,
            )}
            render={
              <HeaderLink
                analyticsId={item.analyticsId}
                href={item.href}
                text={item.label}
                analyticsText={item.analyticsText}
                aria-describedby={tooltipId}
              />
            }
          >
            {floating ? (
              <>
                {item.label}
                {item.isExternal && <span aria-hidden>→</span>}
              </>
            ) : (
              <>
                {item.label} {item.isExternal && <RightArrowIcon />}
              </>
            )}
          </Button>
        );
        return tooltipId && item.tooltip ? (
          <Tooltip tooltip={item.tooltip} align="end" id={tooltipId}>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })()}
    </NavigationMenu.Item>
  );
};

// Icon button with tooltip
const IconButtonItem = ({
  item,
  dataTestId,
}: {
  item: Extract<NavItem, { type: "iconButton" }>;
  dataTestId?: string;
}) => (
  <NavigationMenu.Item data-testid={dataTestId}>
    <Tooltip tooltip={item.label}>
      <HeaderLink
        analyticsId={item.analyticsId}
        href={item.href}
        text={item.label}
        analyticsText={item.analyticsText}
        aria-label={item.label}
        className="hover:bg-nav-hover flex h-10 w-10 items-center rounded-md"
      >
        {item.icon}
      </HeaderLink>
    </Tooltip>
  </NavigationMenu.Item>
);

// Custom item without dropdown
const CustomItemSimple = ({
  item,
  dataTestId,
}: {
  item: Extract<NavItem, { type: "custom" }>;
  dataTestId?: string;
}) => <li data-testid={dataTestId}>{item.customItem}</li>;

// Standard item with dropdown (links or custom submenu)
const StandardItemWithDropdown = ({
  item,
  dataTestId,
}: {
  item: Extract<NavItem, { type: "dropdown" | "customDropdown" }>;
  dataTestId?: string;
}) => {
  const { variant } = useHeaderContext();
  const floating = variant === "floating";
  return (
    <NavigationMenu.Item
      key={item.label}
      value={item.label}
      data-testid={dataTestId}
    >
      <NavigationMenu.Trigger
        className={cn(
          "flex items-center gap-1 whitespace-nowrap rounded-md",
          floating
            ? cn(
                navHoverBg,
                "data-popup-open:bg-[var(--nav-hover,var(--color-chalk))] h-10 pl-3 pr-1",
              )
            : "hover:bg-nav-hover data-popup-open:bg-nav-hover py-2 pl-3 pr-1",
        )}
      >
        <Text
          variant={floating ? undefined : "btnLg"}
          className={cn("flex items-center", floating && floatingNavLabelClass)}
        >
          {item.label}
          <NavigationMenu.Icon
            className={cn(
              "data-popup-open:rotate-180",
              floating && cn("size-[18px] opacity-60", navFg),
            )}
          >
            {floating ? <ChevronDownOutlineIcon /> : <ChevronDownIcon />}
          </NavigationMenu.Icon>
        </Text>
      </NavigationMenu.Trigger>

      <NavigationMenu.Content
        keepMounted
        className={cn("border-nav-border/30")}
      >
        {item.type === "dropdown" ? (
          floating ? (
            <FloatingDropdownLinks links={item.links} />
          ) : (
            <DropdownLinks links={item.links} />
          )
        ) : (
          item.customSubmenu
        )}
      </NavigationMenu.Content>
    </NavigationMenu.Item>
  );
};

// Standard item without dropdown (simple link)
const StandardItemSimpleLink = ({
  item,
  dataTestId,
}: {
  item: Extract<NavItem, { type: "link" }>;
  dataTestId?: string;
}) => {
  const { variant } = useHeaderContext();
  const floating = variant === "floating";
  return (
    <NavigationMenu.Item key={item.label} data-testid={dataTestId}>
      <HeaderLink
        analyticsId={item.analyticsId}
        href={item.href}
        text={item.label}
        analyticsText={item.analyticsText}
        className={cn(
          "flex h-10 items-center whitespace-nowrap rounded-md px-3",
          floating ? navHoverBg : "hover:bg-nav-hover",
        )}
      >
        <Text
          variant={floating ? undefined : "btnLg"}
          className={cn(floating && floatingNavLabelClass)}
        >
          {item.label}
        </Text>
      </HeaderLink>
    </NavigationMenu.Item>
  );
};

export const DesktopNavItem = ({ item, dataTestId }: DesktopNavItemProps) => {
  switch (item.type) {
    case "iconButton":
      return <IconButtonItem item={item} dataTestId={dataTestId} />;
    case "customWithDropdown":
      return <CustomItemWithDropdown item={item} dataTestId={dataTestId} />;
    case "button":
      return <ButtonItem item={item} dataTestId={dataTestId} />;
    case "custom":
      return <CustomItemSimple item={item} dataTestId={dataTestId} />;
    case "dropdown":
    case "customDropdown":
      return <StandardItemWithDropdown item={item} dataTestId={dataTestId} />;
    case "link":
      return <StandardItemSimpleLink item={item} dataTestId={dataTestId} />;
  }
};
